import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "@carbon/react";
import ESOganisationDropdown from "../components/dropdown/esOrganisationDropdown";
import SectionHeader from "../components/section/SectionHeader";

import {
  EDIT_ORGANISATION_TRANSLATION_FORM_FIELDS,
  ORG_TRANSLATION_ITEMS,
  ORGANIZATION_TRANSLATION_TABLE_HEADERS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  errorAlert,
  makeApiRequest,
  successAlert,
  transformOrganizationTranslationRows,
  transformTranslationUpdateBody,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import OrgContext from "../store/orgContext";
import InfoAlert from "../ui/InfoAlert";
import TableActionButton from "../ui/TableActionButton";
import {
  deleteTranslation,
  getTranslationByRef,
  updateTranslationByRef,
} from "../api";
import EditRow from "../components/dataTable/EditRow";
import { INITIAL_ORGANIZATION_TRANSLATION_EDIT_STATE } from "../constants/editState";
import useHideSidePanel from "../hooks/useHideSidePanel";
import {
  DELETE_ORG_TRANSLATION_TRANSLATIONS,
  READ_ORG_TRANSLATION_TRANSLATIONS,
} from "../translations/util";
import useAppTitle from "../hooks/useAppTitle";
import BulkUpload from "../components/bulkUpload/BulkUpload";

const OrganizationTranslation = () => {
  const {
    token,
    scopes: { org_translations: orgTranslation },
  } = useContext(AuthContext);
  const {
    orgs: orgList,
    selectedOrg,
    updateOrg,
    updateTranslationType,
    selectedTranslationType,
  } = useContext(OrgContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  useAppTitle(
    "Organisation Translations- IWF Config Admin Tool (ICAT) by AT&T"
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [translationData, setTranslationData] = useState<any[]>([]);

  const { showSidePanel, handleToggleSidePanel } = useHideSidePanel();
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);

  const [selectedColumn, setSelectedColumn] = useState<{
    id: string;
    value: string;
  } | null>(null);

  // State to check if Bulk Upload is Clicked
  const [isBulkUploadEnabled, setIsBulkUploadEnabled] = useState(false);

  const fetchTranslation = async () => {
    setLoading(true);
    try {
      if (selectedTranslationType && selectedOrg) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response = await makeApiRequest<any[]>({
          path: `/api/v1/app-config/translations/${selectedTranslationType.id}`,
          token,
          org: selectedOrg.id,
        });
        setTranslationData(response);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
      setTranslationData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTranslation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, selectedTranslationType]);

  const deleteConnection = async (type: string, ref: string) => {
    if (selectedOrg && token) {
      try {
        await deleteTranslation({
          token,
          org: selectedOrg.id,
          ref,
          type,
        });
        successAlert(DELETE_ORG_TRANSLATION_TRANSLATIONS.successAlertMessage);
        handleToggleSidePanel();
        setSelectedConnectionId(null);
        fetchTranslation();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      }
    }
  };

  return (
    <>
      <SectionHeader
        pageTitle={READ_ORG_TRANSLATION_TRANSLATIONS.header}
        pageDescription={READ_ORG_TRANSLATION_TRANSLATIONS.headerSubtext}
        sideComponent={
          <div className="dropdown-details">
            <ESOganisationDropdown
              org={selectedOrg || undefined}
              orgsList={orgList}
              setOrg={updateOrg}
            />
            {selectedOrg ? (
              <Dropdown
                className="translation-dropdown"
                data-cy="translation-dropdown"
                titleText={
                  READ_ORG_TRANSLATION_TRANSLATIONS.translationDropdown
                    .titleText
                }
                label={
                  READ_ORG_TRANSLATION_TRANSLATIONS.translationDropdown.label
                }
                selectedItem={selectedTranslationType}
                items={ORG_TRANSLATION_ITEMS}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                itemToString={(item: { text: any }) => (item ? item.text : "")}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={({ selectedItem }: { selectedItem: any }) => {
                  updateTranslationType(selectedItem);
                }}
              />
            ) : null}
          </div>
        }
      />

      {selectedOrg && selectedTranslationType ? (
        <>
          {showSidePanel &&
          selectedOrg &&
          selectedConnectionId &&
          !isBulkUploadEnabled ? (
            <EditRow
              panelName={READ_ORG_TRANSLATION_TRANSLATIONS.editRow.panelName}
              initialState={INITIAL_ORGANIZATION_TRANSLATION_EDIT_STATE}
              org={selectedOrg.id}
              esRef={selectedConnectionId}
              setShowEditPanel={handleToggleSidePanel}
              hasDeletePermission={
                orgTranslation && orgTranslation.includes("d")
              }
              hasEditPermission={orgTranslation && orgTranslation.includes("u")}
              onDelete={() =>
                deleteConnection(
                  selectedTranslationType.id,
                  selectedConnectionId
                )
              }
              formFields={EDIT_ORGANISATION_TRANSLATION_FORM_FIELDS}
              header={READ_ORG_TRANSLATION_TRANSLATIONS.editRow.header}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              transformResponseRow={(res: any) => res}
              transformUpdateBody={transformTranslationUpdateBody}
              onUpdate={updateTranslationByRef(selectedTranslationType.id)}
              loadDataByRef={getTranslationByRef(selectedTranslationType.id)}
              refreshTable={fetchTranslation}
              deleteModalItemName="ID"
            />
          ) : null}

          {showSidePanel && isBulkUploadEnabled ? (
            <BulkUpload
              moduleName="Organisation Translation"
              pathName={`/api/v1/app-config/translations/${selectedTranslationType.id}`}
              csvTemplateFile={[
                "PRIORITY-INCOMING,EMERGENCY-FLAG-INCOMING,EMERGENCY-FLAG-OUTGOING,PRIORITY-OUTGOING-VALUE",
              ]}
              closeBulkUploadSidePanel={() => {
                handleToggleSidePanel();
                setIsBulkUploadEnabled(false);
                fetchTranslation();
              }}
            />
          ) : null}

          <DataTableWithControls
            loading={loading}
            filterColumns={[]}
            selectedColumn={selectedColumn}
            setSelectedColumn={setSelectedColumn}
            onSearch={() => null}
            showTableControls={orgTranslation && orgTranslation.includes("c")}
            showCreateButton
            showAdvancdSearch={false}
            showSearch={false}
            onCreateButtonClick={() => {
              navigate(`/configurationManagement/OrganizationTranslation/new`);
            }}
            rows={transformOrganizationTranslationRows(translationData).map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (row: any) => ({
                ...row,
                action: (
                  <TableActionButton
                    onActionButtonClick={() => {
                      setSelectedConnectionId(row.id);
                      handleToggleSidePanel();
                    }}
                  />
                ),
              })
            )}
            headers={ORGANIZATION_TRANSLATION_TABLE_HEADERS}
            showPagination={false}
            showBulkUploadButton={
              orgTranslation && orgTranslation.includes("c")
            }
            onBulkUploadButtonClick={() => {
              handleToggleSidePanel();
              setIsBulkUploadEnabled(true);
            }}
          />
        </>
      ) : (
        <InfoAlert
          heading={READ_ORG_TRANSLATION_TRANSLATIONS.infoAlert.heading}
          subText={READ_ORG_TRANSLATION_TRANSLATIONS.infoAlert.subText}
        />
      )}
    </>
  );
};

export default OrganizationTranslation;
